import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { ElementsEventListenerService } from '../../services/elements-event-listener.service';
import { ColorDeciderService } from '../../services/color-decider.service';
import { FlagsService } from '../../services/channel-flags.service';
import {
    updatedChannelEvent,
    disableStatusUpdated,
    flashingStatusUpdated,
    openChannelEvent,
    closeChannelEvent,
    flashChannelEvent,
    visualStateEvent,
} from '../../models/events';

@Component({
    selector: 'lib-mimic-viewer',
    templateUrl: 'mimic-viewer.component.html',
    styleUrls: ['./mimic-viewer.component.css'],
    standalone: false,
})
export class MimicViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() mimic;
    @Input() width: string;
    @Input() height: string;
    @Input() elementsSettings: any[];
    @Input() elementIntegrations: any[];
    @Input() channelsUpdateObservable: Observable<any>;
    @Input() updatedChannelObservable: Observable<any> | Subject<any>;
    @Input() defaultColors;
    @Input() vesselId: string;
    @Input() channelList: any[];

    @Output() createLibraryComponentElement = new EventEmitter();

    @ViewChild('dataContainer') dataContainer: ElementRef;

    private updatedChannelSubscription: Subscription;
    private elementIntegrationMap = new Map();
    private destroySubscriptionSubject$ = new Subject<void>();

    private channelSubscribers = new Map<string, Array<(channelUpdated: any) => void>>();
    private channelsUpdateSubscription: Subscription;

    constructor(
        private cdr: ChangeDetectorRef,
        private eventListenerService: ElementsEventListenerService,
        private colorDeciderService: ColorDeciderService,
        private flagsService: FlagsService
    ) {}

    ngOnInit(): void {
        this.setupMainSubscription();
        this.eventListenerService
            .onCreateLibraryComponentElement()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(event => {
                this.createLibraryComponentElement.emit({
                    currentElement: event.elem,
                    elementIntegration: event.elementIntegration,
                });
            });
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (this.mimic && !changes.mimic?.isFirstChange()) {
            this.loadHmi(this.mimic);
            this.adjustSvgSize();
        }

        if (changes.channelList && this.channelList?.length) {
            await this.flagsService.updateChannelList(this.channelList);
            this.eventListenerService.clear();
            this.clearElementSubscriptions();
            this.loopElementChildren(this.dataContainer.nativeElement);
        }
    }

    async ngAfterViewInit() {
        await this.flagsService.updateChannelList(this.channelList);
        if (this.mimic) {
            this.colorDeciderService.setDefaultColors(this.defaultColors);
            this.loadHmi(this.mimic);
        }

        this.updatedChannelSubscription = this.updatedChannelObservable.subscribe(async channel => {
            await this.flagsService.updateChannel(channel);
            this.onChannelUpdated(channel);
        });

        this.adjustSvgSize();
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
        this.updatedChannelSubscription?.unsubscribe();
        this.clearElementSubscriptions();
        this.eventListenerService.clear();
    }

    private adjustSvgSize() {
        const container = this.dataContainer.nativeElement as HTMLElement;
        const svg = container.querySelector('svg') as SVGElement;
        if (!svg) return;

        const [, , origW, origH] = svg.getAttribute('viewBox')!.split(' ');
        const originalWidth = +origW;
        const originalHeight = +origH;
        const parentW = container.clientWidth;
        const parentH = container.clientHeight;
        const scale = Math.min(parentW / originalWidth, parentH / originalHeight);

        svg.setAttribute('width', `${originalWidth * scale}px`);
        svg.setAttribute('height', `${originalHeight * scale}px`);
    }

    public loadHmi(mimic) {
        if (!mimic.svgcontent) return;
        this.dataContainer.nativeElement.innerHTML = '';

        const newDoc = this.text2xml(mimic.svgcontent);
        const svg = document.adoptNode(newDoc.documentElement) as SVGElement;
        const bkColor = mimic.profile.bkcolor;

        this.dataContainer.nativeElement.append(svg);

        this.elementIntegrationMap = this.mapByElementId(this.elementIntegrations);
        this.eventListenerService.clear();

        this.clearElementSubscriptions();
        this.loopElementChildren(this.dataContainer.nativeElement);

        svg.style.background = bkColor;
        svg.style.overflow = 'hidden';

        this.cdr.detectChanges();
    }

    private onChannelUpdated(channel) {
        const index = this.channelList?.findIndex(ch => ch.rowId === channel.rowId);
        if (index >= 0) this.channelList[index] = { ...this.channelList[index], ...channel };

        this.elementIntegrations
            ?.filter(integration => integration.elementSetting?.channelTag === channel.rowId)
            .forEach(integration => {
                const elem = this.dataContainer.nativeElement.querySelector(`[id="${integration.elementId}"]`);
                if (elem) this.eventListenerService.updateValueUnits(elem, channel);
            });
    }

    private clearElementSubscriptions() {
        this.channelSubscribers.clear();
    }

    private loopElementChildren(elem: Element) {
        if (elem.children.length > 0) {
            Array.from(elem.children).forEach((child: Element) => {
                const elementType = child.getAttribute('data-icreate-type');
                const controlType = child.getAttribute('type');
                if (elementType || controlType) {
                    this.attachEventListers(child);
                } else this.loopElementChildren(child);
            });
        } else this.attachEventListers(elem);
    }

    private attachEventListers(elem: Element) {
        const elementId = elem.getAttribute('id');
        const elementIntegration = this.elementIntegrationMap.get(elementId);

        if (elementIntegration) {
            this.eventListenerService.attachEventListerners(
                elem,
                elementIntegration.elementSetting,
                elementIntegration,
                this.vesselId,
                this.channelList,
                this.defaultColors
            );

            const registerHandler = (channelId: string, action: (ch: any) => void) => {
                if (!channelId) return;
                if (!this.channelSubscribers.has(channelId)) {
                    this.channelSubscribers.set(channelId, []);
                }
                this.channelSubscribers.get(channelId).push(action);
            };

            if (elementIntegration.elementSetting) {
                const settings = elementIntegration.elementSetting;

                registerHandler(settings.channelTag, ch => this.eventTrigger(elem, ch, updatedChannelEvent));
                registerHandler(settings.disabledStatus?.channelTag, ch =>
                    this.eventTrigger(elem, ch, disableStatusUpdated)
                );
                registerHandler(settings.flashingStatus?.channelTag, ch =>
                    this.eventTrigger(elem, ch, flashingStatusUpdated)
                );
                registerHandler(settings.valveSetting?.open?.channelTag, ch =>
                    this.eventTrigger(elem, ch, openChannelEvent)
                );
                registerHandler(settings.valveSetting?.close?.channelTag, ch =>
                    this.eventTrigger(elem, ch, closeChannelEvent)
                );
                registerHandler(settings.valveSetting?.open?.flashChannelTag, ch =>
                    this.eventTrigger(elem, ch, flashChannelEvent)
                );
            } else if (elementIntegration.elementCondition) {
                let logicChannelIdMapIndex = new Map();
                let logicChannelList = [];

                elementIntegration.elementCondition.logicList?.forEach((logic, index) => {
                    const chId = logic?.matrixChannel?.channel?.rowId;
                    logicChannelIdMapIndex.set(chId, index);

                    registerHandler(chId, ch => {
                        const findedIndex = logicChannelIdMapIndex.get(ch.id);
                        if (findedIndex !== undefined) {
                            logicChannelList.push({ ...ch, index: findedIndex });
                            if (logicChannelIdMapIndex.size === logicChannelList.length) {
                                this.eventTrigger(elem, logicChannelList, visualStateEvent);
                                logicChannelList = [];
                            }
                        }
                    });
                });
            }
        }
    }

    private eventTrigger(elem, channelUpdated, event) {
        let customEvent;
        if (event === visualStateEvent) {
            customEvent = new CustomEvent(event, {
                detail: channelUpdated,
            });
        } else {
            customEvent = new CustomEvent(event, {
                detail: {
                    value: channelUpdated.value,
                    id: channelUpdated.id,
                    flags: channelUpdated.flags,
                },
            });
        }
        elem.dispatchEvent(customEvent);
    }

    private mapByElementId(elementsSettings: any[]) {
        const elementSettingsMap = new Map();
        for (let i = 0; i < elementsSettings.length; i++) {
            elementSettingsMap.set(elementsSettings[i].elementId, elementsSettings[i]);
        }
        return elementSettingsMap;
    }

    private text2xml(sXML) {
        if (sXML.includes('<svg:svg')) {
            sXML = sXML.replace(/<(\/?)svg:/g, '<$1').replace('xmlns:svg', 'xmlns');
        }
        let out;
        let dXML;
        try {
            dXML = new DOMParser();
            dXML.async = false;
        } catch (e) {
            throw new Error('XML Parser could not be instantiated');
        }
        try {
            out = dXML.parseFromString(sXML, 'text/xml');
        } catch (e2) {
            throw new Error('Error parsing XML string');
        }
        return out;
    }

    private setupMainSubscription() {
        if (!this.channelsUpdateObservable || this.channelsUpdateSubscription) return;

        this.channelsUpdateSubscription = this.channelsUpdateObservable.subscribe(channelsUpdated => {
            this.flagsService.updateFlags(channelsUpdated);

            for (let i = 0; i < channelsUpdated.length; i++) {
                const channel = channelsUpdated[i];
                if (!channel || !channel.id) continue;

                const handlers = this.channelSubscribers.get(channel.id);
                if (handlers) {
                    handlers.forEach(handlerFn => handlerFn(channel));
                }
            }
        });
    }
}
